import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Languages } from "lucide-react";
import { adminCreateBlog, adminUpdateBlog } from "../../lib/api";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";

const bodyToText = (body) =>
  (body || []).map((s) => `## ${s.h2}\n${(s.p || []).join("\n")}`).join("\n\n");
const faqToText = (faq) => (faq || []).map((f) => `${f.q} | ${f.a}`).join("\n");

const fromPost = (p) => {
  if (!p) {
    return { title: "", excerpt: "", description: "", body: "", faq: "",
      image: "", author: "Velmira Living", date: "", slug: "" };
  }
  return {
    title: p.title?.tr || "",
    excerpt: p.excerpt?.tr || "",
    description: p.description?.tr || "",
    body: bodyToText(p.body?.tr),
    faq: faqToText(p.faq?.tr),
    image: p.image || "",
    author: p.author || "Velmira Living",
    date: p.date || "",
    slug: p.slug || "",
  };
};

const inputCls = "bg-background border-border/60";

export const BlogForm = ({ initial, onSaved, onCancel }) => {
  const [f, setF] = useState(() => fromPost(initial));
  const [saving, setSaving] = useState(false);
  const editing = !!initial;
  const set = (k, v) => setF((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!f.title.trim() || !f.body.trim()) {
      toast.error("Başlık ve içerik gereklidir / Title and body are required");
      return;
    }
    setSaving(true);
    toast.info("Çeviriliyor (EN + FR)… / Translating…");
    try {
      if (editing) await adminUpdateBlog(initial.id, f);
      else await adminCreateBlog(f);
      toast.success(editing ? "Yazı güncellendi" : "Yazı yayınlandı");
      onSaved();
    } catch (err) {
      toast.error("Kaydedilemedi / Could not save");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} data-testid="blog-form" className="flex flex-col gap-5">
      <div className="flex items-start gap-2 border border-primary/30 bg-primary/5 text-primary/90 text-sm p-3">
        <Languages size={16} className="shrink-0 mt-0.5" />
        <span>Türkçe yazın — İngilizce ve Fransızca çeviriler kaydederken otomatik oluşturulur. (Write in Turkish; EN & FR are auto-translated on save.)</span>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Başlık / Title (TR)</Label>
        <Input data-testid="blog-title" className={inputCls} value={f.title} onChange={(e) => set("title", e.target.value)} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Özet / Excerpt (TR)</Label>
        <Textarea data-testid="blog-excerpt" rows={2} className={inputCls} value={f.excerpt} onChange={(e) => set("excerpt", e.target.value)} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">İçerik / Body (TR)</Label>
        <p className="text-[11px] text-muted-foreground -mt-1">Başlıklar için satır başına <code className="text-primary">## Başlık</code> yazın; diğer satırlar paragraf olur.</p>
        <Textarea data-testid="blog-body" rows={12} className={`${inputCls} font-mono text-sm`} value={f.body} onChange={(e) => set("body", e.target.value)} placeholder={"## Neden Nice?\nNice, Akdeniz iklimiyle...\n\n## Süreç\nAlım noter üzerinden yürür..."} />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">SSS / FAQ (TR) — opsiyonel</Label>
        <p className="text-[11px] text-muted-foreground -mt-1">Her satır: <code className="text-primary">Soru | Cevap</code></p>
        <Textarea data-testid="blog-faq" rows={3} className={inputCls} value={f.faq} onChange={(e) => set("faq", e.target.value)} placeholder={"Yabancılar ev alabilir mi? | Evet, Fransa'da kısıtlama yoktur."} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="flex flex-col gap-1.5 sm:col-span-1">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Yazar / Author</Label>
          <Input data-testid="blog-author" className={inputCls} value={f.author} onChange={(e) => set("author", e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Tarih / Date (YYYY-MM-DD)</Label>
          <Input data-testid="blog-date" type="date" className={inputCls} value={f.date} onChange={(e) => set("date", e.target.value)} />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs uppercase tracking-wide text-muted-foreground">Slug (opsiyonel)</Label>
          <Input data-testid="blog-slug" className={inputCls} value={f.slug} onChange={(e) => set("slug", e.target.value)} placeholder="auto from title" />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label className="text-xs uppercase tracking-wide text-muted-foreground">Görsel URL / Image URL</Label>
        <Input data-testid="blog-image" className={inputCls} value={f.image} onChange={(e) => set("image", e.target.value)} />
      </div>

      <div className="flex items-center gap-3 pt-1">
        <button type="submit" disabled={saving} data-testid="blog-save" className="inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-60 px-8 py-3 uppercase tracking-[0.2em] text-xs font-body transition-all duration-300">
          {saving ? <><Loader2 size={15} className="animate-spin" /> Çeviriliyor & kaydediliyor…</> : editing ? "Güncelle & Yeniden Çevir" : "Yayınla (Otomatik Çeviri)"}
        </button>
        <button type="button" onClick={onCancel} data-testid="blog-cancel" className="px-6 py-3 uppercase tracking-[0.2em] text-xs font-body border border-border/60 text-foreground/70 hover:text-foreground transition-colors">
          İptal
        </button>
      </div>
    </form>
  );
};

export default BlogForm;
